/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo, useState } from "react";
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import {
  Box,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import api from "../../../api";
import { Loader } from "../../../components/loader";
import { Query_Keys } from "../../../lib/queryKeys";
import moment from "moment";
import { StyledSubTitle } from "../../employee_groups/group_members/styles";
import { CalendarGrid } from "./calendar_grid";
import { MonthGrid } from "./month_grid";
import { YearGrid } from "./year_grid";
import { EventDetailDialog } from "./event_detail_dialog";
import {
  buildCardsLookup,
  buildOccurrences,
  formatMonthLabel,
  formatWeekLabel,
  formatYearLabel,
  getMonthGridDays,
  groupOccurrencesByDate,
} from "./calendar_utils";
import { calendarTheme } from "./calendar_theme";

const CALENDAR_VIEW_OPTIONS = [
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
]

export const CalendarSection = ({ restaurantGroupIds = [], scopeType, onNavigateToDiscounts }) => {
  const [selectedOccurrence, setSelectedOccurrence] = useState(null)
  const [viewType, setViewType] = useState('week')
  const [anchorDate, setAnchorDate] = useState(() => moment())

  const normalizedRestaurantGroupIds = useMemo(
    () => Array.from(new Set((restaurantGroupIds || []).filter(Boolean))),
    [restaurantGroupIds]
  )

  const fetchMatrix = async () => {
    const response = await api.get('/v1/admin/dynamic_discount_events/matrix', {
      params: {
        restaurant_group_ids: normalizedRestaurantGroupIds.length ? normalizedRestaurantGroupIds : undefined,
        scope_type: scopeType || undefined,
      }
    })

    return response?.data
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: Query_Keys.dynamic_discount_events_matrix({
      restaurant_group_ids: normalizedRestaurantGroupIds,
      scope_type: scopeType,
    }),
    queryFn: fetchMatrix,
    staleTime: 5 * 60 * 1000,
  })

  const cardsLookup = useMemo(() => buildCardsLookup(data?.cards), [data?.cards])
  const occurrences = useMemo(() => buildOccurrences(data?.events, cardsLookup), [cardsLookup, data?.events])
  const occurrencesByDate = useMemo(() => groupOccurrencesByDate(occurrences), [occurrences])

  const weekStart = useMemo(() => anchorDate.clone().startOf('week'), [anchorDate])
  const weekDays = useMemo(() => (
    Array.from({ length: 7 }, (_, index) => weekStart.clone().add(index, 'days'))
  ), [weekStart])

  const weekOccurrences = useMemo(() => (
    weekDays.reduce((accumulator, day) => {
      accumulator[day.format('YYYY-MM-DD')] = occurrencesByDate[day.format('YYYY-MM-DD')] || []
      return accumulator
    }, {})
  ), [weekDays, occurrencesByDate])

  const monthStart = useMemo(() => anchorDate.clone().startOf('month'), [anchorDate])
  const monthDays = useMemo(() => getMonthGridDays(monthStart), [monthStart])

  const yearStart = useMemo(() => anchorDate.clone().startOf('year'), [anchorDate])

  const viewLabelConfig = useMemo(() => ({
    week: { label: formatWeekLabel(weekStart), testid: 'dynamic-discounts-calendar-week-label' },
    month: { label: formatMonthLabel(monthStart), testid: 'dynamic-discounts-calendar-month-label' },
    year: { label: formatYearLabel(yearStart), testid: 'dynamic-discounts-calendar-year-label' },
  }[viewType]), [viewType, monthStart, yearStart, weekStart])

  const hours = useMemo(() => (
    Array.from({ length: calendarTheme.endHour - calendarTheme.startHour + 1 }, (_, index) => calendarTheme.startHour + index)
  ), [])

  const calendarHeight = hours.length * calendarTheme.hourRowHeight

  const handlePrevious = () => setAnchorDate((current) => current.clone().subtract(1, viewType))
  const handleNext = () => setAnchorDate((current) => current.clone().add(1, viewType))
  const handleToday = () => setAnchorDate(moment())

  const handleDrillToWeek = (day) => {
    setAnchorDate(day.clone())
    setViewType('week')
  }

  const handleDrillToMonth = (day) => {
    setAnchorDate(day.clone())
    setViewType('month')
  }

  return (
    <Box sx={{ marginTop: '30px'}} data-testid="dynamic-discounts-calendar-section">

      {isLoading ? (
        <Loader height={64} sx={{ py: 6 }} />
      ) : isError ? (
        <Box
          sx={{
            borderRadius: '20px',
            backgroundColor: '#ffffff',
            border: '1px solid #ececec',
            padding: 4,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '15px', color: '#b91c1c', fontWeight: 600, marginBottom: 1 }}>
            Unable to load the dynamic perq calendar.
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
            Refresh the page or try a different restaurant group filter.
          </Typography>
        </Box>
      ) : !occurrences.length ? (
        <Box
          sx={{
            borderRadius: '20px',
            backgroundColor: '#ffffff',
            border: '1px solid #ececec',
            padding: 4,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: '15px', color: '#111827', fontWeight: 600, marginBottom: 1 }}>
            No calendar events available.
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#6b7280' }}>
            Create active dynamic discount events to populate the calendar view.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: '20px',
            backgroundColor: '#ffffff',
            border: '1px solid #ececec',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              px: { xs: 2, md: 3 },
              py: 2.5,
              borderBottom: '1px solid #f1f5f9',
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                Dynamic Perq Calendar
              </Typography>
              <StyledSubTitle>
                Overview of all overruled perqs
              </StyledSubTitle>
            </Box>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  backgroundColor: calendarTheme.colors.background,
                  border: '1px solid #ececec',
                  borderRadius: '12px',
                  padding: '4px',
                }}
              >
                {CALENDAR_VIEW_OPTIONS.map((option) => {
                  const isActive = viewType === option.key

                  return (
                    <Box
                      key={option.key}
                      component="button"
                      type="button"
                      onClick={() => setViewType(option.key)}
                      data-testid={`dynamic-discounts-calendar-view-${option.key}`}
                      aria-pressed={isActive}
                      sx={{
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        padding: '6px 16px',
                        fontSize: '13px',
                        fontWeight: 500,
                        fontFamily: 'Inter, sans-serif',
                        color: isActive ? '#f97316' : '#18181B',
                        backgroundColor: isActive ? '#fff7ed' : 'transparent',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: isActive ? '#fff7ed' : '#f9fafb',
                        },
                      }}
                    >
                      {option.label}
                    </Box>
                  )
                })}
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={handlePrevious}
                  size="small"
                  aria-label={`Go to previous ${viewType}`}
                  data-testid="dynamic-discounts-calendar-previous"
                >
                  <ArrowBackIosNewRoundedIcon sx={{ fontSize: '16px' }} />
                </IconButton>
                <Typography
                  sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', minWidth: 150, textAlign: 'center' }}
                  data-testid={viewLabelConfig.testid}
                >
                  {viewLabelConfig.label}
                </Typography>
                <IconButton
                  onClick={handleNext}
                  size="small"
                  aria-label={`Go to next ${viewType}`}
                  data-testid="dynamic-discounts-calendar-next"
                >
                  <ArrowForwardIosRoundedIcon sx={{ fontSize: '16px' }} />
                </IconButton>
                <IconButton
                  onClick={handleToday}
                  aria-label={`Go to current ${viewType}`}
                  size="small"
                  data-testid="dynamic-discounts-calendar-today-button"
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '10px',
                    border: `1px solid ${calendarTheme.colors.todayButtonBorder}`,
                    backgroundColor: calendarTheme.colors.background,
                    color: calendarTheme.colors.headingText,
                    boxShadow: calendarTheme.shadows.todayButton,
                    '&:hover': {
                      backgroundColor: calendarTheme.colors.todayButtonHover,
                      borderColor: calendarTheme.colors.todayButtonBorder,
                    },
                  }}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {viewType === 'month' ? (
            <MonthGrid
              monthStart={monthStart}
              monthDays={monthDays}
              occurrencesByDate={occurrencesByDate}
              onOpenOccurrence={setSelectedOccurrence}
              onDrillToWeek={handleDrillToWeek}
            />
          ) : viewType === 'year' ? (
            <YearGrid
              yearStart={yearStart}
              occurrencesByDate={occurrencesByDate}
              onDrillToMonth={handleDrillToMonth}
            />
          ) : (
            <CalendarGrid
              weekDays={weekDays}
              weekOccurrences={weekOccurrences}
              hours={hours}
              calendarHeight={calendarHeight}
              onOpenOccurrence={setSelectedOccurrence}
            />
          )}
        </Box>
      )}

      <EventDetailDialog
        occurrence={selectedOccurrence}
        onClose={() => setSelectedOccurrence(null)}
        onNavigateToDiscounts={onNavigateToDiscounts}
      />
    </Box>
  )
}
